import React from 'react'
import { Dialog } from '@headlessui/react'
import { 
  XMarkIcon, 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BriefcaseIcon, 
  BanknotesIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline'

interface Employee {
  id: number
  employeeNumber: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  department: string
  position: string
  employmentStatus: 'active' | 'inactive' | 'terminated'
  employmentType: 'full_time' | 'part_time' | 'contract'
  baseSalary: string
  salaryCurrency: 'USD' | 'CDF'
  hireDate: string
  manager?: {
    id: number
    firstName: string
    lastName: string
    employeeNumber: string
  }
}

interface Props {
  employee: Employee
  onClose: () => void
}

const EmployeeProfileModal: React.FC<Props> = ({ employee, onClose }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[#63d400]/20 text-[#63d400] border border-[#63d400]/30'
      case 'inactive':
        return 'bg-amber-100 text-amber-800 border border-amber-200'
      case 'terminated':
        return 'bg-red-100 text-red-800 border border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200'
    }
  }

  const getStatusDot = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[#63d400]'
      case 'inactive':
        return 'bg-amber-500'
      case 'terminated':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getEmploymentTypeLabel = (type: string) => {
    switch (type) {
      case 'full_time':
        return 'Full Time'
      case 'part_time':
        return 'Part Time'
      case 'contract':
        return 'Contract'
      default:
        return type
    }
  }

  const getEmploymentTypeColor = (type: string) => {
  if (!type) return "bg-gray-100 text-gray-600 border border-gray-200";

  switch (type.toLowerCase()) {
    case "full-time":
    case "permanent":
      return "bg-green-100 text-green-800 border border-green-200";

    case "part-time":
      return "bg-yellow-100 text-yellow-800 border border-yellow-200";

    case "contract":
    case "consultant":
      return "bg-blue-100 text-blue-800 border border-blue-200";

    case "intern":
    case "temporary":
      return "bg-purple-100 text-purple-800 border border-purple-200";

    default:
      return "bg-gray-100 text-gray-800 border border-gray-200";
  }
  };

  const formatSalary = (salary: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(salary))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getTenure = (hireDate: string) => {
    const hire = new Date(hireDate)
    const now = new Date()
    const years = now.getFullYear() - hire.getFullYear()
    const months = now.getMonth() - hire.getMonth()
    
    if (months < 0) {
      return `${years - 1} years, ${12 + months} months`
    }
    return `${years} years, ${months} months`
  }

  return (
    <Dialog open={true} onClose={() => {}} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        
        {/* Overlay */}
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />

        {/* Modal Content */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto z-50 overflow-hidden transform transition-all">
          
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-[#000065] to-[#000065]/90 px-6 py-5">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 bg-gradient-to-br from-[#63d400] to-[#52b800] rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {employee.firstName[0]}{employee.lastName[0]}
                </div>
                <div>
                  <Dialog.Title className="text-xl font-bold text-white">
                    {employee.firstName} {employee.lastName}
                  </Dialog.Title>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center text-[#63d400] text-sm">
                      <IdentificationIcon className="h-4 w-4 mr-1" />
                      #{employee.employeeNumber}
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(employee.employmentStatus)}`}>
                      <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${getStatusDot(employee.employmentStatus)}`}></span>
                      {employee.employmentStatus.charAt(0).toUpperCase() + employee.employmentStatus.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
            
            {/* Contact Information Card */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-[#000065] uppercase tracking-wide mb-4 flex items-center">
                <UserIcon className="h-4 w-4 mr-2 text-[#63d400]" />
                Contact Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <EnvelopeIcon className="h-4 w-4 mr-3 text-gray-500" />
                  <div>
                    <p className="text-gray-600">{employee.email}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Email Address</p>
                  </div>
                </div>
                {employee.phone && (
                  <div className="flex items-center text-sm">
                    <PhoneIcon className="h-4 w-4 mr-3 text-gray-500" />
                    <div>
                      <p className="text-gray-600">{employee.phone}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Phone Number</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Job Information Card */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-[#000065] uppercase tracking-wide mb-4 flex items-center">
                <BriefcaseIcon className="h-4 w-4 mr-2 text-[#63d400]" />
                Job Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Position</p>
                    <p className="text-sm font-medium text-[#000065] mt-0.5">{employee.position}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Department</p>
                    <p className="text-sm font-medium text-[#000065] mt-0.5">{employee.department}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500">Employment Type</p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${getEmploymentTypeColor(employee.employmentType)}`}>
                    {getEmploymentTypeLabel(employee.employmentType)}
                  </span>
                </div>

                {employee.manager && (
                  <div>
                    <p className="text-xs text-gray-500">Reports To</p>
                    <div className="flex items-center mt-1">
                      <div className="h-6 w-6 bg-[#000065] rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                        {employee.manager.firstName[0]}{employee.manager.lastName[0]}
                      </div>
                      <p className="text-sm font-medium text-[#000065]">
                        {employee.manager.firstName} {employee.manager.lastName}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Employment Details Card */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-[#000065] uppercase tracking-wide mb-4 flex items-center">
                <BuildingOfficeIcon className="h-4 w-4 mr-2 text-[#63d400]" />
                Employment Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Hire Date</p>
                    <div className="flex items-center mt-1">
                      <CalendarIcon className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm font-medium text-[#000065]">{formatDate(employee.hireDate)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tenure</p>
                    <p className="text-sm font-medium text-[#63d400] mt-0.5">{getTenure(employee.hireDate)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Compensation Card */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-[#000065] uppercase tracking-wide mb-4 flex items-center">
                <BanknotesIcon className="h-4 w-4 mr-2 text-[#63d400]" />
                Compensation
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Base Salary</p>
                  <p className="text-lg font-bold text-[#63d400] mt-0.5">
                    {formatSalary(employee.baseSalary, employee.salaryCurrency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">per month</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Currency</p>
                  <p className="text-sm font-medium text-[#000065] mt-0.5">
                    {employee.salaryCurrency}
                    {employee.salaryCurrency === 'USD' ? ' ($)' : ' (FC)'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-[#000065]/5 to-[#63d400]/5 rounded-xl p-5 border border-[#000065]/10">
              <h3 className="text-sm font-semibold text-[#000065] uppercase tracking-wide mb-4 flex items-center">
                <ChartBarIcon className="h-4 w-4 mr-2 text-[#63d400]" />
                Employee Summary
              </h3>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-[#000065]">
                    {getEmploymentTypeLabel(employee.employmentType).split(' ')[0]}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Employment</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-[#63d400]">
                    {employee.employmentStatus === 'active' ? '✓' : '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Status</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
              <button
                onClick={onClose}
                className="px-5 py-2 rounded-xl text-sm font-medium bg-[#000065] text-white hover:bg-[#000065]/90 transition-colors shadow-sm"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default EmployeeProfileModal